import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fingerprint, ScanFace, Smartphone, ShieldCheck } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { passkeyRegisterSchema } from "@/schemas/passkeySchema";
import { usePasskeyRegister } from "@/hooks/auth/usePasskeyQueries";

// Feature highlights shown in the modal body
const PASSKEY_FEATURES = [
    {
        icon: ScanFace,
        label: "Face ID / Touch ID",
        description: "Use your device biometrics",
    },
    {
        icon: Smartphone,
        label: "QR Code",
        description: "Scan from another device",
    },
    {
        icon: ShieldCheck,
        label: "Phishing-resistant",
        description: "Works only on this site",
    },
];

/**
 * RegisterPasskeyModal
 *
 * Modal dialog for registering a new passkey from the account settings page.
 * Collects a friendly name, then triggers the browser's WebAuthn ceremony.
 *
 * Props:
 *   open         {boolean}  — controlled open state
 *   onOpenChange {function} — called when dialog should open/close
 */
export default function RegisterPasskeyModal({ open, onOpenChange }) {
    const { mutate: registerPasskey, isPending } = usePasskeyRegister();

    const form = useForm({
        resolver: zodResolver(passkeyRegisterSchema),
        defaultValues: { friendlyName: "" },
    });

    const onSubmit = ({ friendlyName }) => {
        registerPasskey(
            { friendlyName },
            {
                onSuccess: () => {
                    form.reset();
                    onOpenChange(false);
                },
            },
        );
    };

    const handleOpenChange = (isOpen) => {
        if (!isOpen) form.reset();
        onOpenChange(isOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:max-w-md">

                {/* ── Hero header ─────────────────────────────────────── */}
                <div className="relative flex flex-col items-center px-8 pt-10 pb-6 text-center"
                    style={{
                        background: "linear-gradient(135deg, #1e3a8a 0%, #2546A1 60%, #3b63d4 100%)",
                    }}>

                    {/* Subtle decorative circles */}
                    <div className="absolute top-[-40px] right-[-40px] h-40 w-40 rounded-full bg-white/5" />
                    <div className="absolute bottom-[-20px] left-[-20px] h-24 w-24 rounded-full bg-white/5" />

                    {/* Icon */}
                    <div className="relative mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/15 shadow-lg ring-1 ring-white/20 backdrop-blur-sm">
                        <Fingerprint className="h-10 w-10 text-white" strokeWidth={1.5} />
                        {/* Pulse ring */}
                        <span className="absolute inset-0 animate-ping rounded-2xl bg-white/10" />
                    </div>

                    <DialogTitle className="relative text-xl font-bold tracking-tight text-white">
                        Register a passkey
                    </DialogTitle>
                    <p className="relative mt-1.5 text-sm leading-relaxed text-blue-100/80">
                        Sign in instantly with your fingerprint,<br />
                        face, or a connected device — no password.
                    </p>
                </div>

                {/* ── Feature chips ────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-2 border-b border-gray-100 bg-gray-50 px-6 py-4">
                    {PASSKEY_FEATURES.map(({ icon: Icon, label, description }) => (
                        <div key={label}
                            className="flex flex-col items-center gap-1.5 rounded-xl bg-white p-3 text-center shadow-sm ring-1 ring-gray-100">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
                                <Icon className="h-4 w-4 text-[#2546A1]" strokeWidth={1.75} />
                            </div>
                            <span className="text-[11px] font-semibold leading-tight text-gray-700">
                                {label}
                            </span>
                            <span className="text-[10px] leading-tight text-gray-400">
                                {description}
                            </span>
                        </div>
                    ))}
                </div>

                {/* ── Form ─────────────────────────────────────────────── */}
                <div className="bg-white px-6 pb-6 pt-5">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

                            <FormField
                                control={form.control}
                                name="friendlyName"
                                render={({ field, fieldState }) => (
                                    <FormItem className="space-y-1.5">
                                        <label className="ml-1 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                            Give this passkey a name
                                        </label>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                placeholder='e.g. "My iPhone" or "Work laptop"'
                                                disabled={isPending}
                                                className={`h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 placeholder:text-gray-400 transition-all duration-200 focus-visible:border-[#2546A1]/40 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#2546A1]/15 ${
                                                    fieldState.error
                                                        ? "border-red-300 bg-red-50 focus-visible:ring-red-100"
                                                        : ""
                                                }`}
                                            />
                                        </FormControl>
                                        <FormMessage className="ml-1 text-xs text-red-500" />
                                    </FormItem>
                                )}
                            />

                            {/* Actions */}
                            <div className="flex gap-2.5 pt-1">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleOpenChange(false)}
                                    disabled={isPending}
                                    className="flex-1 rounded-xl border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer">
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    disabled={isPending}
                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#2546A1] text-sm font-semibold text-white shadow-md shadow-blue-900/20 hover:bg-[#1e3a8a] cursor-pointer">
                                    {isPending ? (
                                        <>
                                            <Spinner className="h-4 w-4 text-white" />
                                            <span>Registering...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Fingerprint className="h-4 w-4" strokeWidth={1.75} />
                                            <span>Register passkey</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </DialogContent>
        </Dialog>
    );
}